import type { ListBuilderConfiguration } from '../../types';
import { allCompetitorsList } from './lists/all-competitors';
import { inactiveCompetitorsList } from './lists/inactive';
import { myAccountsCompetitorsList } from './lists/my-accounts';
import { primaryAtOurCustomersCompetitorsList } from './lists/primary-at-our-customers';
import { recentlyAddedCompetitorsList } from './lists/recently-added';

/**
 * PD-17066 — the Competitors page (Sales → Competitors). Rows come from
 * GET /api/competitors (CompetitorsService), which applies every filter here
 * itself. "Quoting against us" waits for competitors on opportunities and
 * jobs (sections 4 and 5 of the sales relationships plan).
 */
export const COMPETITOR_LIST_CONFIG: ListBuilderConfiguration = {
	lists: [
		allCompetitorsList,
		myAccountsCompetitorsList,
		primaryAtOurCustomersCompetitorsList,
		recentlyAddedCompetitorsList,
		inactiveCompetitorsList,
	],
	paging: {
		defaultPageSize: 25,
		pageSizeOptions: [25, 50],
	},
	filters: {
		activeFilters: [
			'competitorKeywords',
			'competitorRelationship',
			'competitorProductGroups',
			'competitorSalespeople',
			'competitorTeams',
			'competitorCustomerStates',
			'competitorHasPrices',
			'competitorAlsoVendor',
			'competitorCustomerCount',
			'competitorEstimatedSpend',
			'competitorLastConfirmed',
			'competitorAddedDate',
			'competitorStatus',
			// The Recently Added tab's locked predicate; hidden on that tab.
			'competitorRecentlyAdded',
		],
		defaultFilters: {},
	},
	objects: [
		{
			baseUrl: '/competitors/:id',
			objectLabel: 'Competitor',
			objectTable: 'wholesaler',
			// Rows are wholesaler records keyed by `id`, so any configured column
			// of the table can be filled in server-side (ListFieldHydrationService).
			rowIdField: 'id',
			selectedFields: [
				{ fieldName: '_id', fieldLabel: 'ID' },
				{ fieldName: 'name', fieldLabel: 'Name' },
				{ fieldName: 'website', fieldLabel: 'Website' },
				{ fieldName: 'phone', fieldLabel: 'Phone' },
				{ fieldName: 'address1', fieldLabel: 'Address' },
				{ fieldName: 'city', fieldLabel: 'City' },
				{ fieldName: 'state', fieldLabel: 'State' },
				{ fieldName: 'zip', fieldLabel: 'Zip' },
				{ fieldName: 'note', fieldLabel: 'Note' },
				{ fieldName: 'customerCount', fieldLabel: 'Customers' },
				{ fieldName: 'primaryCount', fieldLabel: 'Primary Customers' },
				{ fieldName: 'secondaryCount', fieldLabel: 'Secondary Customers' },
				{ fieldName: 'alsoBuysCount', fieldLabel: 'Also Buys From' },
				{
					fieldName: 'estimatedAnnualSpend',
					fieldLabel: 'Estimated Annual Spend',
				},
				{ fieldName: 'priceCount', fieldLabel: 'Prices Logged' },
				{ fieldName: 'lastPriceDate', fieldLabel: 'Last Price Logged' },
				{ fieldName: 'lastConfirmedDate', fieldLabel: 'Last Confirmed' },
				{ fieldName: '_created_date', fieldLabel: 'Created Date' },
			],
		},
	],
	tableSorts: [
		{
			id: 'name',
			name: 'Name',
			fields: [{ direction: 'ASC', fieldName: 'name', objectTable: 'wholesaler' }],
			description: 'Sort competitors by name',
		},
		{
			id: 'customers-desc',
			name: 'Most Customers',
			fields: [
				{ direction: 'DESC', fieldName: 'customerCount', objectTable: 'wholesaler' },
			],
			description: 'Competitors at the most customers first',
		},
		{
			id: 'primary-desc',
			name: 'Most Primary Customers',
			fields: [
				{ direction: 'DESC', fieldName: 'primaryCount', objectTable: 'wholesaler' },
			],
			description: 'Competitors that are primary at the most customers first',
		},
		{
			id: 'spend-desc',
			name: 'Largest Estimated Spend',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'estimatedAnnualSpend',
					objectTable: 'wholesaler',
				},
			],
			description: 'Largest estimated spend with them first',
		},
		{
			id: 'prices-desc',
			name: 'Most Prices Logged',
			fields: [
				{ direction: 'DESC', fieldName: 'priceCount', objectTable: 'wholesaler' },
			],
			description: 'Competitors with the most logged prices first',
		},
		{
			id: 'confirmed-asc',
			name: 'Least Recently Confirmed',
			fields: [
				{
					direction: 'ASC',
					fieldName: 'lastConfirmedDate',
					objectTable: 'wholesaler',
				},
			],
			description: 'Competitors whose customer links were confirmed longest ago first',
		},
		{
			id: 'created-desc',
			name: 'Newest First',
			fields: [
				{ direction: 'DESC', fieldName: '_created_date', objectTable: 'wholesaler' },
			],
			description: 'Most recently added competitors first',
		},
	],
};
